import React, { useRef, useState } from 'react'
import { uploadBaseCaseModelCsv, BaseCaseModelUploadResponse } from '../../../apis/apis'
import { pushToast } from '../../../components/Toaster/Toaster.slice'
import { useAppDispatch } from '../../../shared/redux/hooks'
import Button from '../../../components/Form/Button'
import Panel from '../../../components/Panel'
import Layout from '../../programs/Layout'

export const BaseCaseModelUpload: React.FC = () => {
    const dispatch = useAppDispatch()
    const hiddenFileInputRef = useRef<HTMLInputElement>(null)
    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [isUploading, setIsUploading] = useState(false)
    const [uploadResult, setUploadResult] = useState<BaseCaseModelUploadResponse | null>(null)
    const [previewMode, setPreviewMode] = useState(true)
    const [nameResolutions, setNameResolutions] = useState<Record<string, string>>({})

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (file) {
            // Validate file type (CSV or Excel)
            if (!file.name.match(/\.(csv|xlsx|xls)$/i)) {
                dispatch(
                    pushToast({
                        message: 'Please select a CSV or Excel file (.csv, .xlsx, .xls)',
                        type: 'error'
                    })
                )
                return
            }
            setSelectedFile(file)
            setUploadResult(null)
            setNameResolutions({})
        }
    }

    const handleUpload = async (shouldPersist: boolean) => {
        if (!selectedFile) {
            dispatch(
                pushToast({
                    message: 'Please select a file first',
                    type: 'error'
                })
            )
            return
        }

        setIsUploading(true)
        setPreviewMode(!shouldPersist)

        try {
            const result = await uploadBaseCaseModelCsv(selectedFile, shouldPersist, nameResolutions)
            setUploadResult(result)

            // If fuzzy matches found, show message and don't persist
            if (result.fuzzyMatches && result.fuzzyMatches.length > 0) {
                dispatch(
                    pushToast({
                        message: `Found ${result.fuzzyMatches.length} name(s) that need resolution. Please select matches below.`,
                        type: 'error'
                    })
                )
            } else if (result.success) {
                dispatch(
                    pushToast({
                        message: shouldPersist
                            ? `Successfully uploaded ${result.recordsCreated} records`
                            : `Preview: ${result.recordsProcessed} records validated`,
                        type: 'success'
                    })
                )
            } else {
                dispatch(
                    pushToast({
                        message: `Upload completed with errors. See details below.`,
                        type: 'error'
                    })
                )
            }
        } catch (error: any) {
            dispatch(
                pushToast({
                    message: error.response?.data?.message || 'Failed to upload file',
                    type: 'error'
                })
            )
        } finally {
            setIsUploading(false)
        }
    }

    const handleFuzzyMatchSelection = (field: string, inputValue: string, cuid: string) => {
        const key = `${field}_${inputValue}`
        setNameResolutions((prev) => {
            // If clicking the same radio button, deselect it
            if (prev[key] === cuid) {
                const { [key]: _, ...rest } = prev
                return rest
            }
            // Otherwise, select it
            return {
                ...prev,
                [key]: cuid
            }
        })
    }

    const allFuzzyMatchesResolved = () => {
        if (!uploadResult?.fuzzyMatches) return true
        return uploadResult.fuzzyMatches.every((match) => {
            const key = `${match.field}_${match.inputValue}`
            return nameResolutions[key] !== undefined
        })
    }

    const handleClearFile = () => {
        setSelectedFile(null)
        setUploadResult(null)
        setNameResolutions({})
        if (hiddenFileInputRef.current) {
            hiddenFileInputRef.current.value = ''
        }
    }

    const hasResults = uploadResult !== null

    const handleDownloadTemplate = () => {
        const link = document.createElement('a')
        link.href = '/base_case_model_template.csv'
        link.download = 'base_case_model_template.csv'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    return (
        <Layout title="Base Case Model">
            <Panel header="Upload File" className="mb-5">
                <div className="flex items-start justify-between mb-4">
                    <p className="text-slate-600 dark:text-slate-300 flex-1">
                        Upload a CSV or Excel file containing base case model data. You can preview the data before saving to the
                        database.
                    </p>
                    <Button onClick={handleDownloadTemplate} color="slate" className="ml-4 whitespace-nowrap">
                        Download Template
                    </Button>
                </div>

                {/* File Input */}
                <div className="mb-4">
                    <input
                        type="file"
                        ref={hiddenFileInputRef}
                        onChange={handleFileSelect}
                        accept=".csv,.xlsx,.xls"
                        className="hidden"
                        disabled={hasResults}
                    />

                    <div className="flex items-center gap-4">
                        <Button onClick={() => hiddenFileInputRef.current?.click()} disabled={isUploading || hasResults}>
                            Choose File (CSV or Excel)
                        </Button>

                        {selectedFile && (
                            <div className="flex items-center gap-4">
                                <span className={`text-sm ${hasResults ? 'text-slate-400 dark:text-slate-500' : 'text-slate-700 dark:text-slate-300'}`}>
                                    <strong>Selected:</strong> {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)}{' '}
                                    KB)
                                </span>
                                {!hasResults && (
                                    <button
                                        onClick={handleClearFile}
                                        className="text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                        disabled={isUploading}
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Action Buttons */}
                {selectedFile && !uploadResult && (
                    <div className="flex gap-4">
                        <Button onClick={() => handleUpload(false)} disabled={isUploading} color="slate">
                            {isUploading && previewMode ? 'Validating...' : 'Preview (Validate Only)'}
                        </Button>
                        <Button onClick={() => handleUpload(true)} disabled={isUploading} color="indigo">
                            {isUploading && !previewMode ? 'Uploading...' : 'Upload & Save'}
                        </Button>
                    </div>
                )}
            </Panel>

            {/* Fuzzy Match Resolution */}
            {uploadResult?.fuzzyMatches && uploadResult.fuzzyMatches.length > 0 && (
                <Panel header="Resolve Name Matches" className="mb-5">
                    <p className="text-slate-600 dark:text-slate-300 mb-4">
                        Some names in your file don't have exact matches in the database. Please select the correct match for each:
                    </p>

                    <div className="space-y-6">
                        {uploadResult.fuzzyMatches.map((match, idx) => {
                            const selectionKey = `${match.field}_${match.inputValue}`
                            const selectedCuid = nameResolutions[selectionKey]
                            const fieldLabel = match.field.charAt(0).toUpperCase() + match.field.slice(1)

                            return (
                                <div key={`${match.field}-${match.inputValue}-${idx}`} className="border border-slate-300 dark:border-slate-600 rounded-lg p-4 bg-slate-50 dark:bg-slate-900">
                                    <div className="mb-3">
                                        <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                                            {fieldLabel}:
                                        </span>
                                        <div className="text-slate-900 dark:text-slate-100 font-medium mt-1">
                                            "{match.inputValue}"
                                        </div>
                                        <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                            (Found in CSV, first occurrence at row {match.rowNumber})
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between mb-2">
                                            <p className="text-sm text-slate-600 dark:text-slate-400">Select the best match:</p>
                                            {selectedCuid && (
                                                <button
                                                    onClick={() => {
                                                        const { [selectionKey]: _, ...rest } = nameResolutions
                                                        setNameResolutions(rest)
                                                    }}
                                                    className="text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 underline"
                                                >
                                                    Clear selection
                                                </button>
                                            )}
                                        </div>
                                        {match.suggestions.map((suggestion) => (
                                            <label
                                                key={suggestion.cuid}
                                                className={`flex items-center gap-3 p-3 border rounded cursor-pointer transition-colors ${
                                                    selectedCuid === suggestion.cuid
                                                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                                                        : 'border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800'
                                                }`}
                                            >
                                                <input
                                                    type="radio"
                                                    name={selectionKey}
                                                    value={suggestion.cuid}
                                                    checked={selectedCuid === suggestion.cuid}
                                                    onChange={() => handleFuzzyMatchSelection(match.field, match.inputValue, suggestion.cuid)}
                                                    className="form-radio text-indigo-600"
                                                />
                                                <div className="flex-1">
                                                    <div className="font-medium text-slate-900 dark:text-slate-100">{suggestion.name}</div>
                                                    <div className="text-xs text-slate-500 dark:text-slate-400">
                                                        Similarity: {Math.round((1 - suggestion.distance / match.inputValue.length) * 100)}%
                                                    </div>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )
                        })}
                    </div>

                    <div className="mt-6 flex gap-4">
                        <Button
                            onClick={() => handleUpload(true)}
                            disabled={!allFuzzyMatchesResolved() || isUploading}
                            color="indigo"
                        >
                            {isUploading ? 'Saving...' : 'Save with Selected Matches'}
                        </Button>
                        <Button onClick={handleClearFile} color="slate">
                            Cancel
                        </Button>
                    </div>
                </Panel>
            )}

            {/* Upload Results */}
            {uploadResult && !uploadResult.fuzzyMatches && (
                <Panel header="Upload Results" className="mb-5">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded border border-slate-200 dark:border-slate-600">
                            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{uploadResult.recordsProcessed}</div>
                            <div className="text-sm text-slate-600 dark:text-slate-400">Records Processed</div>
                        </div>
                        <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded border border-slate-200 dark:border-slate-600">
                            <div className="text-2xl font-bold text-green-600 dark:text-green-400">{uploadResult.recordsCreated}</div>
                            <div className="text-sm text-slate-600 dark:text-slate-400">Records Created</div>
                        </div>
                        <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded border border-slate-200 dark:border-slate-600">
                            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{uploadResult.recordsSkipped}</div>
                            <div className="text-sm text-slate-600 dark:text-slate-400">Records Skipped</div>
                        </div>
                        <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded border border-slate-200 dark:border-slate-600">
                            <div className="text-2xl font-bold text-red-600 dark:text-red-400">{uploadResult.duplicates.length}</div>
                            <div className="text-sm text-slate-600 dark:text-slate-400">Duplicates Found</div>
                        </div>
                    </div>

                    {/* Duplicates */}
                    {uploadResult.duplicates.length > 0 && (
                        <div className="mb-4">
                            <h4 className="font-semibold text-yellow-700 dark:text-yellow-500 mb-2">Duplicate Records:</h4>
                            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded p-4 max-h-48 overflow-y-auto">
                                <ul className="list-disc list-inside space-y-1">
                                    {uploadResult.duplicates.map((dup, idx) => (
                                        <li key={idx} className="text-sm text-yellow-800 dark:text-yellow-400">
                                            {dup}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}

                    {/* Errors */}
                    {uploadResult.errors && uploadResult.errors.length > 0 && (
                        <div className="mb-4">
                            <h4 className="font-semibold text-red-700 dark:text-red-500 mb-2">Errors:</h4>
                            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded p-4 max-h-48 overflow-y-auto">
                                <ul className="list-disc list-inside space-y-1">
                                    {uploadResult.errors.map((error, idx) => (
                                        <li key={idx} className="text-sm text-red-800 dark:text-red-400">
                                            {error}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}

                    {/* Success and next actions */}
                    {uploadResult.success && uploadResult.recordsCreated > 0 && (
                        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded p-4">
                            <p className="text-green-800 dark:text-green-400">
                                ✓ Successfully uploaded {uploadResult.recordsCreated} records to the database.
                            </p>
                        </div>
                    )}

                    {/* Preview mode message */}
                    {!uploadResult.success && uploadResult.recordsProcessed > 0 && uploadResult.recordsCreated === 0 && (
                        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded p-4">
                            <p className="text-blue-800 dark:text-blue-400">
                                Preview completed. Review the results above, then click "Upload & Save" to persist to
                                database.
                            </p>
                        </div>
                    )}

                    <div className="mt-6 flex gap-4">
                        <Button onClick={handleClearFile}>Upload Another File</Button>
                        {uploadResult.recordsCreated === 0 && uploadResult.success && (
                            <Button onClick={() => handleUpload(true)}>Save to Database</Button>
                        )}
                    </div>
                </Panel>
            )}

            {/* Instructions */}
            <Panel header="File Format Requirements">
                <ul className="list-disc list-inside space-y-1 text-sm text-slate-700 dark:text-slate-300">
                    <li>Accepts CSV (.csv) or Excel (.xlsx, .xls) files</li>
                    <li>Excel files will be automatically converted to CSV internally</li>
                    <li>Must include all 35 required columns (Project, Investor, Stage, Date, etc.)</li>
                    <li>Date format: MM/DD/YYYY</li>
                    <li>Numbers can include commas and dollar signs</li>
                    <li>Duplicates are detected based on: Project + Date + Stage</li>
                    <li>Preview mode validates the file without saving to database</li>
                </ul>
            </Panel>
        </Layout>
    )
}
